/** What each record kind is called, and where its detail lives.
 *
 * **One place, because two screens may not describe the same fact differently** —
 * `30-design-principles.md` rule 7. These were inside `Record.tsx` while the record page rendered
 * only activity records, and moved here when every kind got a page and the work tab was shared.
 *
 * **The rule is that `kindLabel` is the only way out of this file**, and it is the module boundary
 * that holds it rather than a sentence: the map itself is no longer exported, so a screen cannot
 * index it and invent its own fallback. That matters because this docstring used to count its
 * readers — *"both maps are read by three modules"* — which was wrong (two, and `Record.tsx`,
 * which it named, imports nothing from here) and would have gone wrong again on the next screen.
 * A census of callers rots; a rule the compiler enforces does not.
 *
 * **The set of kinds is not here, and must not be copied here.** This file used to export a
 * `RECORD_KINDS` list and a `RecordKind` union *"so a screen can be written against the set rather
 * than against a string"*. No screen was: every consumer takes `kind: string`, and both were read
 * by nothing. The authoritative set is the server's enum as the generated
 * `components["schemas"]["RecordKind"]` — a hand-kept second list would drift from it, and the
 * drift would show as a kind this file has a label for and the server does not.
 *
 * The label is a **customer's word**, not the enum's: a `facility` is a *site* and an
 * `expected_source` is an *expected source*, which is what the census calls it on screen.
 */

/** The labels. Not exported, per the rule above: a caller that indexes this map has to invent its
 * own fallback, and the two fallbacks then differ — which is what `RecordWork.tsx` did with
 * `KIND_LABEL[kind] ?? kind`, rendering a raw `activity_record` where the record page beside it
 * rendered *"Activity record"*. */
const KIND_LABEL: Record<string, string> = {
  activity_record: "Activity record",
  climate_risk: "Climate risk",
  document: "Document",
  facility: "Site",
  meter: "Meter",
  expected_source: "Expected source",
  filing_obligation: "Filing obligation",
  disclosure_statement: "Disclosure statement",
  initiative: "Initiative",
  supplier_request: "Supplier request",
};

/** Where each kind's **detail** lives — the screen that owns the facts, which is never this page.
 *
 * A record page shows what is *true about the work on* a record. The facts themselves belong to
 * whoever owns them: a site's address is the organisation screen's, a document's pages are the
 * evidence screen's. Rendering a second copy here is a second thing to keep correct, and the two
 * eventually disagree — the reason the receipt drawer is rule 3's single rendering of a figure.
 *
 * Absent for the two kinds reached *through* a filing rather than listed, so a missing entry
 * renders no link rather than a link to nowhere.
 */
export const KIND_HOME: Record<string, string> = {
  activity_record: "/emissions",
  climate_risk: "/risks",
  document: "/evidence",
  facility: "/organisation",
  meter: "/organisation",
  expected_source: "/sources",
  initiative: "/transition",
  supplier_request: "/suppliers",
};

/** Whether this build knows `kind` as a record kind — and so whether an id of that object type has
 * a page at `/records/{id}`. Asked of the same map `kindLabel` reads, so a kind with a label and a
 * kind with a page cannot come apart; the map itself still does not leave this file. */
export function isRecordKind(kind: string): boolean {
  return Object.hasOwn(KIND_LABEL, kind);
}

/** The label for a kind, never the raw enum value, and never blank.
 *
 * A kind this build has not heard of is a **new server with an old client**, which happens on every
 * rolling deploy. It reads as the value with its underscores removed rather than as an empty header
 * — an absence said, not left blank (rule 5).
 */
export function kindLabel(kind: string | undefined): string {
  if (!kind) return "Record";
  return KIND_LABEL[kind] ?? kind.replace(/_/g, " ");
}
